/**
 * @file AccountType.jsx
 * @description Handles account type selection and configuration during onboarding
 *
 * @section Responsibilities
 * - Present available account types
 * - Show account features and benefits
 * - Handle account configuration
 * - Manage fee structure display
 * - Process account selection
 * - Handle account requirements
 *
 * @section Dependencies
 * - AccountService: Account management
 * - FeeCalculator: Fee calculation
 * - RequirementService: Requirement checking
 * - ConfigurationService: Account configuration
 *
 * @section Integration Points
 * - Account API: Account types
 * - Fee System: Fee calculation
 * - Requirement System: Account requirements
 * - Onboarding Flow: Progress tracking
 *
 * @section State Management
 * - selectedAccount: Chosen account type
 * - configuration: Account configuration
 * - fees: Fee structure
 * - requirements: Account requirements
 *
 * @section Events
 * - onAccountSelect: Account selection
 * - onConfiguration: Account configuration
 * - onFeeCalculation: Fee calculation
 * - onRequirementCheck: Requirement validation
 *
 * @section Props
 * @prop {Array} availableAccounts - List of available account types
 * @prop {Function} onComplete - Completion callback
 * @prop {String} initialSelection - Initially selected account type
 * @prop {Boolean} showFees - Controls fee display
 */

import axios from "axios";
import React, { useRef, useState, useEffect } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/account-type.css";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useForm } from "../../utils/useForm";
import TextInput from "./../../components/forms/TextInput";
import CheckboxInput from "./../../components/forms/CheckboxInput";
import DgButton from "../../components/DgButton";
import TradierAccess from "../Authentication/TradierAccess";

function useElementSize(element) {
  const [elementSize, setElementSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        width: element.current ? element.current.offsetWidth : 0,
        height: element.current ? element.current.offsetHeight : 0,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return elementSize;
}

function AccountType() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.session);

  const [disclosures, setDisclosures] = useState([
    {
      id: 1,
      checked: false,
      text: "Have you read and understood the brokerage platform's terms and conditions, privacy policy, and customer agreements?",
    },
    {
      id: 2,
      checked: false,
      text: "Do you agree to provide a copy of your government-issued identification for identity verification purposes?",
    },
    {
      id: 3,
      checked: false,
      text: "Do you acknowledge that investing in securities involves risks, and you are responsible for understanding those risks before making investment decisions?",
    },
  ]);

  const toggleCheckbox = (id) => {
    const checkbox = disclosures.find((disclosure) => disclosure.id == id);
    checkbox.checked = !checkbox.checked;

    setDisclosures([...disclosures]);
  };

  const ball1 = useRef();
  let ball1Size = useElementSize(ball1);

  const ball2 = useRef();
  let ball2Size = useElementSize(ball2);

  const ball3 = useRef();
  let ball3Size = useElementSize(ball3);

  const [data, setData] = useForm({
    additionAccountHolders: "",
    bankName: "",
    accountNumber: "",
    routingNumber: "",
  });

  const updateUser = () => {
    if (disclosures.some((disclosure) => !disclosure.checked)) {
      setError("Please read and agree to the disclosures before proceeding");
      return;
    }
    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_BASE_URL}/user`, {
        PK: user.PK,
        SK: user.SK,
        ...data,
      })
      .then((res) => {
        navigate("/onboarding/risk-quiz");
      });
  };

  return (
    <div className="account-type">
      <div className="background">
        <div className="cover-background"></div>
        <div className="ball-container">
          <div
            className="ball ball1"
            ref={ball1}
            style={{ height: ball1Size.width }}
          ></div>
          <div
            className="ball ball2"
            ref={ball2}
            style={{ height: ball2Size.width }}
          ></div>
          <div
            className="ball ball3"
            ref={ball3}
            style={{ height: ball3Size.width }}
          ></div>
        </div>
        <div className="content container shadow1">
          <div className="progress-bar step2">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Personalization</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Disclosures</p>
              </div>
            </div>
          </div>
          <div className="inner-content">
            <div className="heading">
              <p className="subtitle">Summary</p>
            </div>
            <div className="summary">
              <img src="/assets/icons/user.png" alt="" />
              <div className="basic-details space">
                <p>
                  <span>Name:</span> {user.fullName}
                </p>
                <p>
                  <span>Address:</span> --
                </p>
                <p>
                  <span>Strategy:</span> --
                </p>
              </div>
              <div className="extra-details space">
                <p>
                  <span>Employment Status:</span> --
                </p>
                <p>
                  <span>Annual income:</span> --
                </p>
              </div>
              <div className="space">
                <div className="outline">
                  <div className="score">
                    <span className="number">0</span>
                    <span className="text">Your Score</span>
                  </div>
                </div>
              </div>
              <img className="edit" src="/assets/icons/pencil.svg" alt="" />
            </div>
            <div className="heading">
              <p className="subtitle">Step 3</p>
              <h1 className="title">Account type and Funding</h1>
            </div>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12} sm={12}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label:
                        "Name(s) of Additional Account Holder(s) (if applicable)",
                      name: "additionAccountHolders",
                      tooltipBool: true,
                      tooltipText: "Additional Account Holder(s).",
                      action: setData,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            <div className="separator"></div>
            <div className="section-heading">
              <h4 className="section-title">Bank account information</h4>
            </div>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12} sm={6}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Bank Name",
                      name: "bankName",
                      tooltipBool: true,
                      tooltipText:
                        "The name of the bank you'll make the investment from.",
                      action: setData,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Account Number",
                      name: "accountNumber",
                      tooltipBool: true,
                      tooltipText:
                        "The number of the account you'll make the investment from.",
                      action: setData,
                    }}
                  ></TextInput>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Routing Number",
                      name: "routingNumber",
                      tooltipBool: true,
                      tooltipText: "Routing number.",
                      action: setData,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            {/* <div className="section-heading">
              <h4 className="section-title">Initial Deposit Amount</h4>
              <p className="section-description">Select a payment method</p>
            </div>
            <div className="payment">
              <div className="method-outline active">
                <div className="method">
                  <p className="method-title">Bank card</p>
                  <img src="/assets/payment/card.svg" alt="" />
                </div>
              </div>
              <div className="method-outline">
                <div className="method">
                  <p className="method-title">Paypal</p>
                  <img src="/assets/payment/paypal.svg" alt="" />
                </div>
              </div>
              <div className="method-outline">
                <div className="method">
                  <p className="method-title">Bank card</p>
                  <img src="/assets/payment/card.svg" alt="" />
                </div>
              </div>
              <div className="method-outline">
                <div className="method">
                  <p className="method-title">Bank card</p>
                  <img src="/assets/payment/card.svg" alt="" />
                </div>
              </div>
            </div>
            <div className="amount">
              <div className="form-field">
                <TextInput
                  customInput={{
                    label: "Amount",
                    name: "amount",
                    value: data.amount,
                    tooltipBool: false,
                    action: setData,
                  }}
                ></TextInput>
              </div>
            </div>
            <div className="button-container">
              <DgButton
                category={"primary"}
                size={"medium"}
                text={`FUND $${data.amount}`}
                action={() => navigate("/onboarding/account-type")}
              />
            </div> */}
            <div className="separator"></div>
            <div className="section-heading">
              <h4 className="section-title">Brokerage platform connection</h4>
            </div>
            <TradierAccess></TradierAccess>
            <div className="separator"></div>
            <div className="section-heading">
              <h4 className="section-title">Disclosures</h4>
            </div>
            <div className="disclosures">
              <div className="form-field checkbox-field">
                <CheckboxInput
                  customInput={{
                    label: "",
                    name: "disclosures",
                    tooltipBool: false,
                  }}
                  data={disclosures}
                  action={toggleCheckbox}
                ></CheckboxInput>
              </div>
            </div>
            <div className="button-container">
              {error && <div className="error">{error}</div>}
              <DgButton
                category={"primary"}
                size={"medium"}
                text={"Finish"}
                action={updateUser}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountType;
